import { getDefaultFormState, RJSFSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import { formsDiffer } from "../common/UnsavedChanges";

/**
 * A deployment with its template fields' defaults filled in, as the template form fills them.
 *
 * The form (rjsf) writes those defaults into the deployment the moment it renders, before
 * anyone types (checked on rjsf 5.24). Comparing the form with the loaded deployment as-is
 * would therefore call a deployment with template defaults changed on arrival. Filling the
 * same defaults into both sides leaves only the user's edits.
 * @param deployment a deployment form state
 * @param schema the project's deployment template, if it has one
 * @returns the deployment with `schema_data` carrying the template's defaults
 */
function withTemplateDefaults(
  deployment: Record<string, unknown>,
  schema: object | undefined,
): Record<string, unknown> {
  if (!schema || Object.keys(schema).length === 0) return deployment;
  return {
    ...deployment,
    schema_data: getDefaultFormState(
      validator,
      schema as RJSFSchema,
      (deployment.schema_data as Record<string, unknown> | undefined) ?? {},
    ),
  };
}

/**
 * Whether the deployment form holds edits it has not saved (AKL-1083).
 * @param deployment the form as it is now
 * @param saved the deployment as last loaded or saved
 * @param schema the project's deployment template, if it has one
 * @returns true when the two differ by more than blanks, key order and template defaults
 */
export function deploymentHasUnsavedChanges(
  deployment: Record<string, unknown>,
  saved: Record<string, unknown>,
  schema: object | undefined,
): boolean {
  return formsDiffer(
    withTemplateDefaults(deployment, schema),
    withTemplateDefaults(saved, schema),
  );
}
